import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import tradeService from '../services/tradeService';

const TradeHistory = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [tradeHistory, setTradeHistory] = useState([]);
  const [totalTrades, setTotalTrades] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Fetch trade history when page, rowsPerPage changes
  useEffect(() => {
    const fetchTradeHistory = async () => {
      try {
        setLoading(true);
        // API pagination is 1-based, but Material-UI is 0-based
        const response = await tradeService.getTradeHistory(page + 1, rowsPerPage);
        setTradeHistory(response.trades);
        setTotalTrades(response.pagination.total);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load trade history', error);
        setError('Failed to load trade history. Please try again later.');
        setLoading(false);
      }
    };

    fetchTradeHistory();
  }, [page, rowsPerPage]);

  // Handle sort column change
  const handleSortRequest = (column) => {
    const isAsc = sortBy === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(column);
    
    // Sort the current data
    const sortedTrades = [...tradeHistory].sort((a, b) => {
      let valueA, valueB;
      
      // Get comparable values based on the sort column
      if (column === 'profit' || column === 'pips') {
        valueA = parseFloat(a[column].replace('+', '').replace(',', ''));
        valueB = parseFloat(b[column].replace('+', '').replace(',', ''));
      } else if (column === 'date') {
        valueA = new Date(a[column]);
        valueB = new Date(b[column]);
      } else {
        valueA = a[column];
        valueB = b[column];
      }
      
      // Compare the values based on sort direction
      if (sortDirection === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });
    
    setTradeHistory(sortedTrades);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await tradeService.getTradeHistory(page + 1, rowsPerPage);
      setTradeHistory(response.trades);
      setTotalTrades(response.pagination.total);
    } catch (error) {
      console.error('Failed to refresh trade history', error);
      setError('Failed to refresh trade history. Please try again later.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Trade History"
        subheader="Past closed positions"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              Total trades: {totalTrades}
            </Typography>
            <Tooltip title="Refresh trade history">
              <IconButton 
                onClick={handleRefresh} 
                disabled={loading || refreshing}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : tradeHistory.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No trade history available.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      onClick={() => handleSortRequest('date')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Date
                        {sortBy === 'date' && (
                          sortDirection === 'asc' 
                            ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> 
                            : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSortRequest('symbol')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Symbol
                        {sortBy === 'symbol' && (
                          sortDirection === 'asc' 
                            ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> 
                            : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Entry</TableCell>
                    <TableCell>Exit</TableCell>
                    <TableCell 
                      onClick={() => handleSortRequest('profit')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Profit/Loss
                        {sortBy === 'profit' && (
                          sortDirection === 'asc' 
                            ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> 
                            : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSortRequest('pips')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Pips
                        {sortBy === 'pips' && (
                          sortDirection === 'asc' 
                            ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> 
                            : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Take Profit and Stop Loss levels">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="inherit">TP/SL</Typography>
                          <InfoIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tradeHistory.map((trade) => (
                    <TableRow key={trade.id} hover>
                      <TableCell>
                        {new Date(trade.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>{trade.symbol}</TableCell>
                      <TableCell>
                        <Chip 
                          label={trade.type} 
                          color={trade.type === 'BUY' ? 'success' : 'error'} 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>{trade.entry}</TableCell>
                      <TableCell>{trade.exit}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: trade.success ? 'success.main' : 'error.main',
                            fontWeight: 600
                          }}
                        >
                          {trade.profit} ({trade.profitPercent})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: trade.success ? 'success.main' : 'error.main',
                            fontWeight: 600
                          }}
                        >
                          {trade.pips}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Take Profit: ${trade.takeProfit} | Stop Loss: ${trade.stopLoss}`}>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            '& > *': { 
                              width: 50, 
                              textAlign: 'center', 
                              borderRadius: 1, 
                              fontSize: '0.75rem',
                              p: 0.5 
                            }
                          }}>
                            <Box sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                              TP
                            </Box>
                            <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                              SL
                            </Box>
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalTrades}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TradeHistory;